import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import ExamInterface from './ExamInterface';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url';
const CAPTURE_INTERVAL = 5000; // 5 seconds for demo (30s in production)
const DEMO_MODE = true; // Set to false when backend is deployed

function App() {
  const [sessionId, setSessionId] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [showExam, setShowExam] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [examId, setExamId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState('');
  const [monitoringActive, setMonitoringActive] = useState(false);
  const [violations, setViolations] = useState([]);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);
  const faceDetectionRef = useRef(null);

  useEffect(() => {
    if (examStarted && !showExam) {
      startCamera();
      startPeriodicCapture();
      loadFaceDetection();
    }
    return () => {
      stopCamera();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
    };
  }, [examStarted, showExam]);

  const loadFaceDetection = async () => {
    // Load face-api.js models for face detection
    try {
      await Promise.all([
        window.faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
        window.faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
        window.faceapi.nets.faceExpressionNet.loadFromUri('/models')
      ]);
      setMonitoringActive(true);
      startFaceDetection();
    } catch (err) {
      console.log('Face detection not available, using demo mode');
      setMonitoringActive(true);
      startDemoDetection();
    }
  };

  const startDemoDetection = () => {
    // Simulate random violations for demo
    faceDetectionRef.current = setInterval(() => {
      const random = Math.random();
      if (random < 0.15) { // 15% chance of violation
        const violationType = Math.random();
        let violation;
        
        if (violationType < 0.4) {
          violation = {
            type: 'HEAD_TURN',
            severity: 'MEDIUM',
            message: 'Head turned away from screen',
            timestamp: new Date().toISOString()
          };
        } else if (violationType < 0.7) {
          violation = {
            type: 'NO_FACE',
            severity: 'HIGH',
            message: 'Face not detected in frame',
            timestamp: new Date().toISOString()
          };
        } else {
          violation = {
            type: 'MULTIPLE_FACES',
            severity: 'CRITICAL',
            message: 'Multiple faces detected',
            timestamp: new Date().toISOString()
          };
        }
        
        setViolations(prev => [...prev, violation]);
        console.log('⚠️ Violation detected:', violation);
      }
    }, 8000); // Check every 8 seconds
  };

  const startFaceDetection = () => {
    faceDetectionRef.current = setInterval(async () => {
      if (videoRef.current && videoRef.current.readyState === 4) {
        try {
          const detections = await window.faceapi
            .detectAllFaces(videoRef.current, new window.faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          let violation = null;

          if (detections.length === 0) {
            violation = {
              type: 'NO_FACE',
              severity: 'HIGH',
              message: 'Face not detected in frame',
              timestamp: new Date().toISOString()
            };
          } else if (detections.length > 1) {
            violation = {
              type: 'MULTIPLE_FACES',
              severity: 'CRITICAL',
              message: `${detections.length} faces detected`,
              timestamp: new Date().toISOString()
            };
          } else {
            // Check head pose using landmarks
            const landmarks = detections[0].landmarks;
            const nose = landmarks.getNose();
            const leftEye = landmarks.getLeftEye();
            const rightEye = landmarks.getRightEye();
            
            // Calculate approximate head turn
            const eyeDistance = Math.abs(leftEye[0].x - rightEye[0].x);
            const noseToLeftEye = Math.abs(nose[0].x - leftEye[0].x);
            const noseToRightEye = Math.abs(nose[0].x - rightEye[0].x);
            
            const ratio = Math.abs(noseToLeftEye - noseToRightEye) / eyeDistance;
            
            if (ratio > 0.3) {
              violation = {
                type: 'HEAD_TURN',
                severity: 'MEDIUM',
                message: 'Excessive head turn detected',
                timestamp: new Date().toISOString()
              };
            }
          }

          if (violation) {
            setViolations(prev => [...prev, violation]);
            console.log('⚠️ Violation detected:', violation);
          }
        } catch (err) {
          console.error('Face detection error:', err);
        }
      }
    }, 3000); // Check every 3 seconds
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access denied:', err);
      alert('Camera access is required for exam proctoring');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  const startExam = async () => {
    if (!examId || !studentId) {
      setError('Please enter both Exam ID and Student ID');
      return;
    }

    setError('');

    // Demo mode - simulate backend response
    if (DEMO_MODE) {
      const mockSessionId = 'demo-' + Math.random().toString(36).substring(7);
      setSessionId(mockSessionId);
      setExamStarted(true);
      // Show exam after camera setup
      setTimeout(() => setShowExam(true), 2000);
      return;
    }

    // Real backend call
    try {
      const response = await axios.post(`${API_BASE_URL}/exam/start`, {
        exam_id: examId,
        student_id: studentId
      });
      
      setSessionId(response.data.session_id);
      setExamStarted(true);
      setTimeout(() => setShowExam(true), 2000);
    } catch (err) {
      console.error('Failed to start exam:', err);
      setError('Failed to start exam session. Please check if the backend is running.');
    }
  };

  const handleEndExam = () => {
    if (window.confirm('Are you sure you want to cancel the exam? Your progress will be lost.')) {
      setExamStarted(false);
      setShowExam(false);
      setSessionId(null);
      setExamId('');
      setStudentId('');
      setViolations([]);
      setMonitoringActive(false);
      stopCamera();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
    }
  };

  if (showExam) {
    return (
      <ExamInterface
        examId={examId}
        studentId={studentId}
        sessionId={sessionId}
        violations={violations}
        onEndExam={handleEndExam}
      />
    );
  }

  const captureAndUpload = async () => {
    if (!videoRef.current || !canvasRef.current || !sessionId) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Demo mode - just log the capture
    if (DEMO_MODE) {
      console.log('📸 Image captured (Demo Mode) - Timestamp:', new Date().toISOString());
      return;
    }
    
    // Real backend upload
    canvas.toBlob(async (blob) => {
      try {
        // Get presigned URL
        const urlResponse = await axios.post(
          `${API_BASE_URL}/exam/upload?session_id=${sessionId}`
        );
        
        // Upload to S3
        await axios.put(urlResponse.data.upload_url, blob, {
          headers: { 'Content-Type': 'image/jpeg' }
        });
        
        console.log('Image uploaded successfully');
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }, 'image/jpeg', 0.8);
  };

  const startPeriodicCapture = () => {
    intervalRef.current = setInterval(captureAndUpload, CAPTURE_INTERVAL);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🎓 AI Exam Proctoring System</h1>
      </header>

      {!examStarted ? (
        <div className="exam-setup">
          <h2>Start Your Exam</h2>
          {DEMO_MODE && (
            <div className="demo-badge">
              🎭 Demo Mode
            </div>
          )}
          <div className="input-group">
            <input
              type="text"
              placeholder="Enter Exam ID (e.g., CS101-2024)"
              value={examId}
              onChange={(e) => setExamId(e.target.value)}
            />
          </div>
          <div className="input-group">
            <input
              type="text"
              placeholder="Enter Student ID (e.g., STU12345)"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
            />
          </div>
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}
          <button onClick={startExam}>
            Begin Exam Session
          </button>
        </div>
      ) : (
        <div className="exam-session">
          <div className="video-container">
            <div className="recording-indicator">
              <div className="recording-dot"></div>
              {monitoringActive ? 'AI Monitoring Active' : 'Initializing...'}
            </div>
            <video ref={videoRef} autoPlay muted />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
          <div className="session-info">
            <h3>Exam Session Active</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Session ID</div>
                <div className="info-value">{sessionId?.substring(0, 8)}...</div>
              </div>
              <div className="info-item">
                <div className="info-label">Exam ID</div>
                <div className="info-value">{examId}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Student ID</div>
                <div className="info-value">{studentId}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Status</div>
                <div className="status-badge">
                  <div className="status-dot"></div>
                  Active
                </div>
              </div>
            </div>
            
            {violations.length > 0 && (
              <div className="violations-panel">
                <h4>⚠️ Violations Detected ({violations.length})</h4>
                <div className="violations-list">
                  {violations.slice(-3).reverse().map((v, idx) => (
                    <div key={idx} className={`violation-item ${v.severity.toLowerCase()}`}>
                      <span className="violation-type">{v.type.replace(/_/g, ' ')}</span>
                      <span className="violation-message">{v.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="privacy-notice">
              <h4>
                <span className="shield-icon">🛡️</span>
                Privacy & Security
              </h4>
              <p>
                AI-powered behavioral analysis active. Images captured every 5 seconds. 
                All data automatically deleted after 30 days.
              </p>
            </div>
            
            <button className="start-exam-btn" onClick={() => setShowExam(true)}>
              Start Exam Questions →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
